/**
 * What the Payouts page reads: which Accounts are rows and who asked for which
 * payout (the CRM's /v1/payout-hub routes), and each Account's balances and
 * payout requests live from OnlyFans (CRM gateway pass-through) or Fansly
 * (the CRM's Fansly shell) — the same calls the Statements and Wallet pages make.
 */
import { api as consoleApi } from '../api/client'
import { CrmError, crmFetch, dataOf } from '../chat/api'
import { isLoggedOutEnvelope } from '../crm/accountSession'
import { createFanslyApi, FanslyError, type FanslyApi } from '../fansly/api'
import { asObj } from '../fansly/data'
import { loadMe } from '../fansly/useMe'
import { balancesOf, methodsOf, payoutsOf } from '../fansly/wallet/walletData'
import { unwrapGateway, type Balances, type Receive } from '../of/statements/statementsData'
import {
  fanslyRequest,
  fanslyRow,
  onlyFansRequest,
  onlyFansRow,
  platformOf,
  type Attempt,
  type PlatformRequest,
  type RowAccount,
  type RowData,
} from './payoutsData'

/** The Account's session is gone: the row shows Not connected instead of an error. */
export class NotConnected extends Error {
  constructor() {
    super('not connected')
    this.name = 'NotConnected'
  }
}

export interface PayoutsApi {
  /** The rows: Accounts with Statements at Full, named as the CRM lists them. */
  accounts: () => Promise<RowAccount[]>
  attempts: () => Promise<Attempt[]>
  /** Throws NotConnected when the Account's session is gone. */
  balances: (account: RowAccount) => Promise<RowData>
  /** The Account's payout requests made at or after `since` (ms). */
  requests: (account: RowAccount, since: number) => Promise<PlatformRequest[]>
}

const OF_PAGE = 20
const FANSLY_PAGE = 25
/** A month of payouts never needs more; a runaway cursor stops here. */
const MAX_PAGES = 5

async function local<T>(path: string): Promise<T> {
  return dataOf<T>(await crmFetch(path, { accountId: '', local: true, method: 'GET' }))
}

/** One OnlyFans call for one Account through the CRM gateway; a logged-out session is NotConnected. */
async function ofCall<T>(accountId: string, path: string, body: Record<string, unknown> = {}): Promise<T> {
  try {
    return unwrapGateway<T>(await crmFetch(path, { accountId, body }))
  } catch (e) {
    if (e instanceof CrmError && isLoggedOutEnvelope(e.body)) throw new NotConnected()
    throw e
  }
}

const fanslyApis = new Map<string, FanslyApi>()
function fanslyApi(accountId: string): FanslyApi {
  let a = fanslyApis.get(accountId)
  if (!a) {
    a = createFanslyApi(accountId, { getToken: () => consoleApi.getToken(), refreshToken: () => consoleApi.refreshAccessToken() })
    fanslyApis.set(accountId, a)
  }
  return a
}

async function fanslyCall<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run()
  } catch (e) {
    if (e instanceof FanslyError && e.disconnected) throw new NotConnected()
    throw e
  }
}

export const payoutsApi: PayoutsApi = {
  async accounts() {
    const [{ account_ids }, crm] = await Promise.all([
      local<{ account_ids: string[] }>('/v1/payout-hub/accounts'),
      local<{ list: Record<string, unknown>[] }>('/v1/accounts'),
    ])
    const allowed = new Set((account_ids ?? []).map(String))
    return (crm.list ?? [])
      .filter((a) => allowed.has(String(a.account_id)))
      .map((a) => ({
        account_id: String(a.account_id),
        name: String(a.profile_name || a.username || a.account_id),
        username: String(a.username ?? ''),
        avatar_url: String(a.avatar_url ?? ''),
        platform: platformOf(String(a.platform ?? '')),
      }))
  },

  async attempts() {
    return (await local<{ attempts: Attempt[] }>('/v1/payout-hub/attempts')).attempts ?? []
  },

  async balances(account) {
    const id = account.account_id
    if (account.platform === 'onlyfans') {
      const [balances, receive] = await Promise.allSettled([ofCall<Balances>(id, '/v1/payouts/balances'), ofCall<Receive>(id, '/v1/payouts/check-receive')])
      if (balances.status === 'rejected') throw balances.reason
      return onlyFansRow(asObj(balances.value) as Balances, receive.status === 'fulfilled' ? (asObj(receive.value) as Receive) : null)
    }
    const fs = fanslyApi(id)
    return fanslyCall(async () => {
      const me = await loadMe(fs)
      const [wallets, earnings, methods] = await Promise.all([
        fs.get<unknown>('/crm/account/wallets', { accountId: me.id }),
        fs.get<unknown>('/crm/creator/earnings/wallet'),
        fs.get<unknown>('/crm/creator/payout-methods'),
      ])
      const b = balancesOf(wallets, earnings)
      return fanslyRow(b.available, b.pending, b.blocked, methodsOf(methods))
    })
  },

  async requests(account, since) {
    const id = account.account_id
    const out: PlatformRequest[] = []
    if (account.platform === 'onlyfans') {
      for (let page = 0; page < MAX_PAGES; page++) {
        const body = asObj(await ofCall<unknown>(id, '/v1/payouts/requests', { limit: OF_PAGE, offset: page * OF_PAGE }))
        const rows = Array.isArray(body.list) ? (body.list as unknown[]).map((r) => onlyFansRequest(id, asObj(r))) : []
        out.push(...rows)
        if (rows.length < OF_PAGE || rows.some((r) => r.createdAt && r.createdAt < since)) break
      }
      return out.filter((r) => r.createdAt >= since)
    }
    const fs = fanslyApi(id)
    return fanslyCall(async () => {
      let before = ''
      for (let page = 0; page < MAX_PAGES; page++) {
        const { rows } = payoutsOf(await fs.get('/crm/creator/payout-requests', { before, after: '', limit: FANSLY_PAGE, offset: 0 }))
        out.push(...rows.map((r) => fanslyRequest(id, r)))
        const last = rows[rows.length - 1]
        if (rows.length < FANSLY_PAGE || !last?.id || (last.createdAt && last.createdAt < since)) break
        before = last.id
      }
      return out.filter((r) => r.createdAt >= since)
    })
  },
}
